export interface DetectedEnv {
  browserName: string;
  rawUA: string;
}

export const SUPPORTED_BROWSERS = ['Edge', 'Chrome', 'Safari'];

function detectBrowser(ua: string): string {
  const u = ua;
  const nav = navigator as any;

  // Brave browser detection - MUST BE FIRST
  if (nav.brave !== undefined) {
    return 'Unknown';
  }

  // Edge (Chromium)
  if (/Edg\//.test(u)) return 'Edge';

  // Chrome on iOS uses CriOS/xxx
  if (/CriOS\//.test(u)) return 'Chrome';

  // Safari (mobile and desktop combined) - exclude Opera Touch (OPT/)
  if (/Version\/.*Safari\//.test(u) && !/Chrome\/|CriOS\/|Edg\/|OPR\/|OPT\/|Opera\//i.test(u)) {
    return 'Safari';
  }

  // Chrome (desktop/android)
  if (/Chrome\//.test(u) && 
      !/Edg\/|OPR\/|OPT\/|Opera|Brave|Vivaldi|UCBrowser|SamsungBrowser|AlohaBrowser|Aloha|DuckDuckGo|YaBrowser|Arc|Maxthon|Kiwi|Epic|Avast|AVG|Sleipnir|Puffin/.test(u)) {
    return 'Chrome';
  }

  return 'Unknown';
}

export function detectEnvironment(nav: Navigator = navigator): DetectedEnv {
  const ua = (nav as any).userAgent || '';
  const browserName = detectBrowser(ua);
  return { browserName, rawUA: ua };
}

export function isSupportedEnvironment(env: DetectedEnv): boolean {
  return SUPPORTED_BROWSERS.includes(env.browserName);
}

/** Detect well-known in-app browsers by UA tokens */
export function detectInAppBrowser(userAgent: string = navigator.userAgent): boolean {
  if (!userAgent) return false;
  const ua = userAgent.toLowerCase();

  const patterns = [
    // Facebook family
    'fban', 'fbav', 'fbios', 'fb_iab', 'fb4a', 'messenger',
    // Instagram
    'instagram',
    // LinkedIn
    'linkedin',
    // WeChat
    'micromessenger', 'wechat',
    // LINE
    'line',
    // WhatsApp
    'whatsapp', 'wawebview',
    // Twitter / X
    'twitter', 'x-client',
    // Others
    'pinterest', 'snapchat', 'tiktok',
    'kakaotalk', 'kakaostory'
  ];

  return patterns.some(p => ua.includes(p));
}

/**
 * Detect device type (Mobile, Tablet, Desktop)
 */
export function detectDeviceType(ua: string = navigator.userAgent): string {
  if (/Mobile|Android|iPhone|iPod/i.test(ua) && !/iPad|Tablet/i.test(ua)) {
    return 'Mobile';
  }
  if (/iPad|Tablet|PlayBook/i.test(ua)) {
    return 'Tablet';
  }
  return 'Desktop';
}

/**
 * Detect OS and version
 */
export function detectOSVersion(ua: string = navigator.userAgent): string {
  // iOS
  const iosMatch = ua.match(/OS (\d+)[_.](\d+)/);
  if (iosMatch) {
    return `iOS ${iosMatch[1]}.${iosMatch[2]}`;
  }

  // Android
  const androidMatch = ua.match(/Android\s+([\d.]+)/);
  if (androidMatch) {
    return `Android ${androidMatch[1]}`;
  }

  // Windows
  const windowsMatch = ua.match(/Windows NT ([\d.]+)/);
  if (windowsMatch) {
    const version = windowsMatch[1];
    const versionMap: { [key: string]: string } = {
      '10.0': '10',
      '6.3': '8.1',
      '6.2': '8',
      '6.1': '7'
    };
    return `Windows ${versionMap[version] || version}`;
  }

  // macOS
  const macMatch = ua.match(/Mac OS X ([\d_]+)/);
  if (macMatch) {
    const version = macMatch[1].replace(/_/g, '.');
    return `macOS ${version}`;
  }

  return 'Unknown OS';
}

/**
 * Detect browser version
 */
export function detectBrowserVersion(ua: string = navigator.userAgent, browserName?: string): string {
  const browser = browserName || detectBrowser(ua);
  let versionMatch: RegExpMatchArray | null = null;

  switch (browser) {
    case 'Chrome':
      versionMatch = ua.match(/(?:Chrome|CriOS)\/([\d.]+)/);
      break;
    case 'Safari':
      versionMatch = ua.match(/Version\/([\d.]+)/);
      break;
    case 'Edge':
      versionMatch = ua.match(/Edg\/([\d.]+)/);
      break;
    default:
      return 'Unknown Browser';
  }

  if (versionMatch && versionMatch[1]) {
    const version = versionMatch[1].split('.').slice(0, 2).join('.');
    return `${browser} ${version}`;
  }

  return `${browser} Unknown`;
}

/**
 * Detect device model
 */
export function detectDeviceModel(ua: string = navigator.userAgent): string {
  // iPhone models
  const iphoneMatch = ua.match(/iPhone(\d+[,_]\d+)?/);
  if (iphoneMatch) {
    return mapIPhoneModel(ua);
  }

  // iPad models
  if (ua.includes('iPad')) {
    return 'iPad';
  }

  // Android device models
  const androidModelMatch = ua.match(/Android.*;\s*([^)]+)\s*Build/);
  if (androidModelMatch && androidModelMatch[1]) {
    const model = androidModelMatch[1].trim();
    // Clean up common prefixes
    return model.replace(/^(SM-|SAMSUNG-|GT-|SGH-)/i, '');
  }

  // Generic fallback
  if (/Android/i.test(ua)) {
    return 'Android Device';
  }
  if (/iPhone/i.test(ua)) {
    return 'iPhone';
  }
  if (/iPad/i.test(ua)) {
    return 'iPad';
  }

  return 'Unknown Device';
}

/**
 * Map iPhone identifier to model name (simplified)
 */
export function mapIPhoneModel(ua: string): string {
  // This is a simplified mapping. Full mapping would require extensive identifier list
  if (ua.includes('iPhone15')) return 'iPhone 15';
  if (ua.includes('iPhone14')) return 'iPhone 14';
  if (ua.includes('iPhone13')) return 'iPhone 13';
  if (ua.includes('iPhone12')) return 'iPhone 12';
  return 'iPhone';
}